/**
 * Shared inline "AI · <reason>" hint (Iter I-14 · Feb 2026).
 *
 * Rendered under a form field whose value has been auto-suggested from
 * uploaded Collar / Assay / Geology / Survey data. Matches the pattern
 * already used for iso-value auto-suggest and variogram Auto-Fit — the
 * user sees WHY a value was pre-filled and can override at any time.
 *
 * Usage:
 *   <input value={commodity} onChange={...} />
 *   {suggestion && (
 *     <AutoFilledHint reason={suggestion.reason}
 *                     source={suggestion.source}
 *                     onAccept={() => setCommodity(suggestion.value)} />
 *   )}
 */
import React from "react";
import { Sparkles, Undo2 } from "lucide-react";

/**
 * @param reason  Short "why this value" string from the backend
 *                (e.g. "Collar file extents · 112 holes").
 * @param source  Optional badge word — `collar`, `assays`, `lithology`,
 *                `drillholes`, `activity`. Rendered as a small tint tag.
 * @param onAccept  Optional callback to re-apply the suggestion after
 *                  the user has edited the field. Rendered as a small
 *                  "restore" button when supplied.
 * @param compact  Set true inside dense grids (table cells) to skip the
 *                  source badge and the "AI ·" prefix.
 */
export default function AutoFilledHint({ reason, source, onAccept, compact = false }) {
  if (!reason) return null;
  return (
    <div className="flex items-center gap-1.5 mt-1 text-[10.5px] font-mono text-muted-foreground"
      data-testid="auto-filled-hint">
      {!compact && <Sparkles className="h-3 w-3 text-primary/70 shrink-0" />}
      {!compact && <span className="text-primary/80 uppercase tracking-[0.14em]">AI</span>}
      <span className="opacity-70">·</span>
      <span className="leading-snug">{reason}</span>
      {source && !compact && (
        <span className="px-1.5 py-[1px] border border-border rounded-none text-[9px] uppercase tracking-widest opacity-75">
          {source}
        </span>
      )}
      {onAccept && (
        <button type="button" onClick={onAccept}
          data-testid="auto-filled-restore"
          title="Restore auto-suggested value"
          className="ml-auto flex items-center gap-1 text-primary/80 hover:text-primary transition">
          <Undo2 className="h-3 w-3" /> restore
        </button>
      )}
    </div>
  );
}
